/**
 * Session stack — supports super-admin "switch to user X → switch back"
 * impersonation. Stack lives in sessionStorage so it doesn't leak across
 * browser sessions but DOES survive a single in-tab navigation.
 *
 * Stores full session payloads (user + token) so popping restores the
 * previous identity exactly.
 */
import { authStrategy, type UserPayload } from './strategy';

const STACK_KEY = 'obd3:auth:stack';

interface StackedSession {
  user: UserPayload;
  token: string | null;
}

function readStack(): StackedSession[] {
  try {
    const raw = sessionStorage.getItem(STACK_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StackedSession[]) : [];
  } catch {
    return [];
  }
}

function writeStack(stack: StackedSession[]): void {
  try {
    sessionStorage.setItem(STACK_KEY, JSON.stringify(stack));
  } catch {
    /* noop */
  }
}

export function pushAndSwitchTo(next: { user: UserPayload; token: string | null }): void {
  const currentUser = authStrategy.getUser();
  const currentToken = authStrategy.getAccessToken();
  if (currentUser) {
    const stack = readStack();
    stack.push({ user: currentUser, token: currentToken });
    writeStack(stack);
  }
  authStrategy.setSession({ user: next.user, token: next.token });
}

export function popPrevious(): StackedSession | null {
  const stack = readStack();
  const previous = stack.pop();
  if (!previous) return null;
  writeStack(stack);
  authStrategy.setSession({ user: previous.user, token: previous.token });
  return previous;
}

export function hasStackedSession(): boolean {
  return readStack().length > 0;
}

export function clearStack(): void {
  try {
    sessionStorage.removeItem(STACK_KEY);
  } catch {
    /* noop */
  }
}
